<template>
  <div class="page-channels" id="page-channels">

    <b-modal
      id="imageGenerateModal"
      ref="imageGenerateModal"
      no-fade
      centered
      static
      size="lg"
      modal-class="form-modal"
      :ok-title="$t('buttons.save')"
      ok-variant="primary"
      :cancel-title="$t('buttons.cancel')"
      cancel-variant="outline-primary"
      :ok-disabled="updatePending || !isUrlValid"
      :cancel-disabled="updatePending"
      :no-close-on-esc="updatePending"
      :no-close-on-backdrop="updatePending"
      :hide-header-close="updatePending"
      @show="modalScroll"
      @hidden="modalScroll"
      @ok.prevent="processForm"
    >
      <template #modal-header-close>
        <icon width="20" height="20" class="d-none d-md-block" type="times"/>
        <icon width="10" height="18" class="d-md-none" type="arrow-left"/>
      </template>
      <template #modal-title>
        {{ $t('pages.project-key.modal.capture-screenshot') }}
      </template>
      <ValidationObserver ref="imageGenerateForm" slim>
        <div class="position-relative-w-100">
          <p class="txt-red">
            {{ $t("pages.project-key.rules.capture-screenshot-desc") }}
            <span class="bold txt-bold">{{ $t("pages.project-key.rules.capture-screenshot-desc-eg") }}</span>
          </p>
          <p class="txt-red">
            {{ $t("pages.project-key.rules.capture-screenshot-desc-tip") }}
          </p>
        </div>
        <div class="edge edge-amount">
          <h3 :content="$t('pages.project-key.modal.webpage-url')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="webpageURL" name="webpageURL" class="mb-0" no-icon no-tooltip
                v-model="webpageURL"
                :disabled="updatePending"
                :placeholder="$t('pages.project-key.modal.enter-webpage-url')"
                :rules="rulesFor('webpageURL')"
              />
            </div>
          </div>
          <p class="txt-red bold txt-bold">
            {{ $t('pages.project-key.rules.image-operation-info') }}
          </p>
        </div>
      </ValidationObserver>
    </b-modal>

    <b-modal
      id="uploadImageModal"
      ref="uploadImageModal"
      no-fade
      centered
      static
      size="lg"
      modal-class="form-modal"
      :ok-title="$t('buttons.save')"
      ok-variant="primary"
      :cancel-title="$t('buttons.cancel')"
      cancel-variant="outline-primary"
      :ok-disabled="updatePending || !isUrlValid || !uploadFile"
      :cancel-disabled="updatePending"
      :no-close-on-esc="updatePending"
      :no-close-on-backdrop="updatePending"
      :hide-header-close="updatePending"
      @show="modalScroll"
      @hidden="modalScroll"
      @ok.prevent="processForm"
    >
      <template #modal-header-close>
        <icon width="20" height="20" class="d-none d-md-block" type="times"/>
        <icon width="10" height="18" class="d-md-none" type="arrow-left"/>
      </template>
      <template #modal-title>
        {{ $t('pages.project-key.modal.upload-image-with-webpage-url') }}
      </template>
      <b-form @submit.prevent="processForm" ref="uploadImageForm">
        <ValidationObserver slim>
          <div class="position-relative-w-100">
            <p class="txt-red">
              {{ $t('pages.project-key.rules.upload-screenshot-desc') }}
              <span class="bold txt-bold">{{ $t('pages.project-key.rules.capture-screenshot-desc-eg') }}</span>
            </p>
            <p class="txt-red">
              {{ $t('pages.project-key.rules.upload-screenshot-desc-tip') }}
            </p>
          </div>
          <div class="edge edge-amount">
            <h3 :content="$t('pages.project-key.modal.webpage-url')"></h3>
            <div class="row row-edit">
              <div class="col">
                <ValidatedField
                  type="text" id="webpageURL" name="webpageURL" class="mb-0" no-icon no-tooltip
                  v-model="webpageURL"
                  :disabled="updatePending"
                  :placeholder="$t('pages.project-key.modal.enter-webpage-url')"
                  :rules="rulesFor('webpageURL')"
                />
              </div>
            </div>
          </div>
        </ValidationObserver>
        <!-- Image Upload Input -->
        <div class="edge edge-amount">
          <h3 :content="$t('pages.project-key.modal.upload-image')"></h3>
          <div class="row row-edit">
            <div class="col">
              <b-form-file
                id="uploadImage"
                v-model="uploadFile"
                accept="image/*"
                :placeholder="$t('pages.project-key.rules.upload-screenshot-placeholder')"
                required
                size="lg"
              ></b-form-file>
            </div>
          </div>
          <p class="txt-red bold txt-bold">
            {{ $t('pages.project-key.rules.image-operation-info') }}
          </p>
        </div>
      </b-form>
    </b-modal>

    <div class="mb-2">
      <Breadcrumb :items=breadCrumbs() v-if="loaded" />
    </div>
    <!-- Search Start -->
    <div class="d-none d-md-flex align-items-center justify-content-end mb-2"
         v-if="tabs.length > 0">
      <div class="header-search d-inline">
        <ValidationObserver ref="userForm" slim>
          <div class="row d-inline-flex d-flex justify-content-start align-items-center">
            <div class="col d-inline-flex">
              <ValidatedField
                ref="searchInput"
                type="text"
                id="search"
                name="search"
                class="mb-0 search-width"
                placeholder="Search translation"
                no-tooltip
                no-icon
                no-validate
                v-model="filter"
                autocomplete="off"
                rules="required|min:3"
                @keyup="handleKeyUp"
              />
              <span v-if="filter" class="clear-icon" @click="clearSearch">
                &times;
              </span>

              <div ref="searchResultsContainer"
                   :class="hideResults ? 'hide-search-results' : 'search-results'"
                   v-if="filter !== '' && filter.length > 0">
                <div v-if="!loaded" class="lessResults">
                  <spinner />
                </div>
                <div v-else-if="loaded && typesenseData.length > 0">
                  <ul class="results-list">
                    <div v-for="result in typesenseData" :key="result.id">
                      <li class="result-item" @click=getSpecificSearchResult(result)>
                        {{
                          result.keywordId + ' ' +
                            result.languageIso + ' | ' +
                            result.pageName + ' > ' +
                            result.translation
                        }}
                      </li>
                    </div>
                  </ul>
                </div>
                <div v-else-if="typesenseData.length === 0" class="lessResults">
                  {{  "No results found" }}
                </div>
              </div>
              <div class="col d-inline-flex">
                <button class="btn btn-primary"
                        :disabled="($refs.userForm && !$refs.userForm.flags.valid) ||
                          (user.language === null && !isAdmin)"
                        @click.prevent="onSearch">
                  Search
                </button>
              </div>
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
    <!-- Search End -->
    <div>
      <tabs :items="tabs" v-model="tab" :with-content="true" @switch="switchTab">
        <template v-for="(languageTab,index) in tabs" v-slot:[tabName(languageTab)]>
          <div class="list d-none d-md-block left-edge float-right mb-3" :key="index">
            <p class="head-line">
              <check-box :disabled="pending" v-model="toBeChecked">
                <span class="ft-14 mr-3" data-toggle="tooltip"
                      :title="$t('pages.project-translations.filters.to-be-Checked')"
                >
                  {{ $t('pages.project-translations.filters.to-be-Checked') }}
                </span>
              </check-box>
              <check-box :disabled="pending" v-model="withoutTranslations">
                <span class="ft-14" data-toggle="tooltip"
                      :title="$t('pages.project-translations.filters.without-translations')"
                >
                  {{ $t('pages.project-translations.filters.without-translations') }}
                </span>
              </check-box>

              <SubmitButton
                @click="filterRecords()"
                variant="primary"
                class="btn-outline-primary ml-2"
                :disabled="pending && !loaded || updatePending"
                :loading="pending"
                inline
              >
                <!-- {{ $t('buttons.search') }} -->
                {{ "Filter Data" }}
              </SubmitButton>
            </p>
          </div>
          <div class="placeHolderString" :key="`placeholder-${index}`">
            <p v-if="languageTab.iso === 'en'" class="txt-red mb-2">
              {{ $t('pages.project-key.rules.placeholder-variable') }}
              <span class="bold txt-bold">{{ $t('pages.project-key.rules.placeholder-variable-eg') }}</span>
            </p>
            <p v-else class="txt-red mb-2">
              {{ $t('pages.project-key.rules.placeholder-variable-not-english') }}
              <span class="bold txt-bold">{{ $t('pages.project-key.rules.placeholder-variable-eg') }}</span>
            </p>
            <p class="txt-red">
              {{ $t('pages.project-key.rules.helpful-tools') }}
              <span class="bold txt-bold">
                <a class="txt-red" target="_blank" href="https://onlinehtmleditor.dev/">
                  {{ $t('pages.project-key.rules.html-editor') }}
                </a>
                <a class="txt-red" target="_blank" href="https://webformatter.com/html">
                  {{ $t('pages.project-key.rules.html-formatter') }}
                </a>
                <a class="txt-red" target="_blank" href="https://html.onlineviewer.net/">
                  {{ $t('pages.project-key.rules.html-viewer') }}
                </a>
              </span>
            </p>
          </div>
          <div class="plans-table" v-if="loaded" :key="index+languageTab">
            <table class="w-100">
              <thead>
                <tr>
                  <th>{{ "Key Id" }}</th>
                  <th class="w-name">{{ $t('pages.project-translations.headers.key') }}</th>
                  <th class="w-name text-center">{{ $t('pages.project-translations.headers.description') }}</th>
                  <th class="w-name">{{ $t('pages.project-translations.headers.translation') }}</th>
                  <th class="w-name text-center">Project Page</th>
                  <th class="w-name">Automated</th>
                </tr>
              </thead>
              <tbody v-if="loaded && filterProjectKeys.length === 0">
                <tr>
                  <td colspan="6" class="w-empty">{{ $t('pages.project-key.no-project-key') }}</td>
                </tr>
              </tbody>
              <tbody v-for="row in filterProjectKeys" :key="row.keywordId">
                <tr class="separator before"></tr>
                <tr>
                  <td>
                    {{row.keywordId}}
                  </td>
                  <td>
                    ${{ row.keywordKey }}
                    <div>
                      <!-- <b>{{ $t('pages.project-translations.headers.image') }}</b> -->
                      <div v-if="row.keywordImages.imageURL" class="mt-1">
                        <a :href="row.keywordImages.webpageURL" target="_blank">
                          {{ $t('pages.project-key.modal.webpage-url') }}
                        </a>
                        <br>
                        <a :href="row.keywordImages.imageURL" target="_blank">
                          <img :src="row.keywordImages.imageURL"
                               :alt="$t('pages.project-translations.placeholders.image-alt')"
                               width="150"
                               height="125"
                          />
                        </a>
                        <div class="mt-1">
                          <SubmitButton
                            @click="removeImage(row)"
                            class="btn-sm btn-danger"
                            :disabled="pending && !loaded || updatePending"
                            :loading="pending"
                            inline
                            v-if="languageTab.iso === 'en' && (isAdmin || isImageEditor)"
                          >
                            {{ $t('buttons.remove') }}
                          </SubmitButton>
                        </div>
                      </div>
                      <div v-else-if="languageTab.iso === 'en'">
                        <img src="\assets\images\noImage.jpg"
                             :alt="$t('pages.project-translations.placeholders.image-alt')"
                             width="150"
                             height="150"
                             class="noImage">
                        <div class="m-1 d-flex flex-nowrap">
                          <SubmitButton
                            @click="openCreateForm(row, languageTab.id, 'uploadImage')"
                            class="btn-sm"
                            variant="secondary"
                            :disabled="pending && !loaded || updatePending"
                            :loading="pending"
                            inline
                            v-if="isAdmin || isImageEditor"
                          >
                            {{ $t('buttons.upload') }}
                          </SubmitButton>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="text-center">
                    <span>{{ descriptionLimited(row.keywordDescription) }} </span>
                    <icon
                      class="icon mr-1 mb-1"
                      width="12" height="12"
                      type="info-circle"
                      v-b-tooltip:page-channels.bottom="row.keywordDescription"
                      v-if="row.keywordDescription.length > 20"
                    />
                  </td>
                  <td class="actions">
                    <p
                      style="text-align: left;"
                      v-bind:style="{color: row.translation.translation ? 'black' : 'red'}">
                      {{ row.englishTranslation }}
                    </p>
                    <p v-if="languageTab.iso !== 'en' && row.translation.draft_translation !== ''">
                      <small>
                        <b>Current published Tranlsation: </b>
                        <span>{{ descriptionLimited(row.translation.translation) }} </span>
                        <icon
                          class="icon mr-1 mb-1"
                          width="12" height="12"
                          type="info-circle"
                          v-b-tooltip:page-channels.bottom="row.translation.translation"
                          v-if="row.translation.translation.length > 20"
                        />
                      </small>
                    </p>
                    <ValidationObserver ref="form-translation">
                      <template v-if="languageTab.iso == 'en' || row.translation.draft_translation === ''" >
                        <ValidatedField
                          class="float-left w-85"
                          :disabled="updatePending"
                          :rules="rulesForRichTextEditor"
                          :type="row.richtextField ? 'richtext': 'textarea'"
                          v-model="row.translation.translation"
                          :placeholder="$t('pages.project-translations.placeholders.add-translation')"
                          :name="`translation-${row.keywordId}`"
                        />
                      </template>
                      <template v-else-if="row.translation.draft_translation !== ''" >
                        <ValidatedField
                          class="float-left w-85"
                          :disabled="updatePending"
                          :rules="rulesForRichTextEditor"
                          :type="row.richtextField ? 'richtext': 'textarea'"
                          v-model="row.translation.draft_translation"
                          :placeholder="$t('pages.project-translations.placeholders.add-translation')"
                          :name="`translation-${row.keywordId}`"
                        />
                      </template>

                      <div class="float-left w-85 ml-1">
                        <template v-if="row.translation.is_automated === 4">
                          <icon
                            class="icon mt-1 mr-2"
                            width="15" height="15"
                            type="info-circle"
                          />
                          <strong class="mt-1 pt-1">
                            {{ $t('pages.project-translations.alert-inDraft') }}
                          </strong>
                        </template>
                      </div>

                      <div class="float-left w-85" v-if="!isImageEditor">
                        <SubmitButton
                          @click="onSubmit(row, languageTab.iso)"
                          variant="secondary"
                          class="btn-save ml-1"
                          :disabled="pending && !loaded || updatePending"
                          :loading="pending"
                          inline
                        >
                          {{ $t('buttons.save') }}
                        </SubmitButton>
                        <template v-if="languageTab.iso !== 'en'
                          && !isTranslationExcluded(languageTab.iso)" >
                          <button class="btn btn-success m-1"
                                  @click="callDeepLApi(row.englishTranslation, languageTab.iso, row)" >
                            Translate with DeepL
                          </button>
                        </template>
                      </div>
                    </ValidationObserver>
                  </td>
                  <td class="text-center">
                    <span>{{ descriptionLimited(row.project_page) }} </span>
                    <icon
                      class="icon mr-1 mb-1"
                      width="12" height="12"
                      type="info-circle"
                      v-b-tooltip:page-channels.bottom="row.project_page"
                      v-if="row.project_page.length > 20"
                    />
                  </td>

                  <template>
                    <td v-if="row.translation.translation === '' ">
                      <span>-</span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 0">
                      <span>
                        <p>Manual</p>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 1">
                      <span>
                        <p>Fetched from DeepL</p>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 2">
                      <span>
                        <p>Changed after DeepL</p>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}}</p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 3">
                      <span>
                        <p class="badge badge-danger" style="font-size: 16px;">To be checked</p><br>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 4">
                      <span>
                        <p class="badge badge-warning" style="font-size: 16px;">In Draft</p><br>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else>
                      <span>-</span>
                    </td>
                  </template>

                </tr>
                <tr class="separator after"></tr>
              </tbody>
            </table>
          </div>
          <pagination
            :key="languageTab+index"
            v-if="loaded && totalCount && setPagination"
            :perPage="20"
            :currentPage="currentPage"
            :totalRows="totalCount"
            @update:current-page="updateCurrentPage($event)"
          />
        </template>
      </tabs>

    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import Swal from 'sweetalert2';

  export default {
    name: 'ProjectTranslations',
    data: () => ({
      uploadFile: null,
      webpageURL: '',
      selectedRow: {},
      tabs: [],
      activeTab: null,
      currentPage: 1,
      disabledSubmit: false,
      filter: '',
      withoutTranslations: false,
      toBeChecked: false,
      setPagination: true,
      debounceTimeout: null,
      hideResults: false,
      specificSearchResultPayload: null,
    }),
    async created() {
      document.addEventListener('click', this.handleClickOutside);
      if (!this.isAdmin) {
        this.tabs.push({ id: this.user.language.id, title: this.user.language.name, iso: this.user.language.iso });
        this.activeTab = this.user.language.id;
      } else {
        await this.fetchLanguages();
        this.languages.forEach((language) => {
          if (!this.activeTab) {
            this.activeTab = language.id;
          }
          this.tabs.push({ id: language.id, title: language.name, iso: language.iso });
        });
      }
      this.resetProjectPageModal();
      await this.show();
      // await this.updateFilter();
      this.updateLangTab();
      this.deepLExcludedLanguages();
    },
    beforeDestroy() {
      document.removeEventListener('click', this.handleClickOutside);
    },
    computed: {
      ...mapGetters('user', ['user', 'isAdmin', 'isImageEditor']),
      ...mapGetters('languages', ['languages']),
      ...mapGetters('projectTranslations', ['loaded', 'noProjectKeys', 'projectKeys', 'deepLExcludedLangs']),
      ...mapState('projectTranslations', ['error', 'pending', 'updatePending', 'totalCount', 'projectPage', 'project']),
      ...mapGetters('typesense', { typesenseLoaded: 'loaded', typesenseSearchResults: 'typesenseSearchResults' }),

      typesenseData() {
        return this.typesenseSearchResults ?? [];
      },
      filterProjectKeys() {
        return this.projectKeys;
      },
      tab: {
        get() { return this.activeTab; },
        set(tab) {
          this.activeTab = tab;
        },
      },
      rulesForRichTextEditor() {
        const rules = {};
        rules.required = true;
        return rules;
      },
      isUrlValid() {
        const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/;
        return urlPattern.test(this.webpageURL);
      },
    },
    methods: {
      ...mapActions('projectTranslations', ['fetchAllTranslations', 'createProjectTranslation', 'updateProjectTranslation', 'getToBeChecked', 'generateKeywordImage', 'uploadKeywordImage', 'removeKeywordImage', 'updateSearchResults', 'updateActiveProjectAndLanguage', 'deepLExcludedLanguages', 'specificSearchResult']),
      ...mapActions('languages', { fetchLanguages: 'fetchData' }),
      ...mapActions('deeplTranslations', ['createDeeplTranslation']),
      ...mapActions('typesense', ['searchRecords', 'resetSearchToEmpty']),
      /** **************************************************
       *
       *  calling api to fetch single translation
       *
       ***************************************************  */
      async callDeepLApi(textString, langCode, row) {
        try {
          const payload = {
            text: textString,
            target_lang: langCode,
            language_id: this.activeTab,
            keyword_id: row.keywordId,
            keywords_translated: row.translation.keywordTranslatedId,
            is_automated: row.translation.is_automated,
            from_products_module: 1,
          };
          // Translate and Save keyword into the DB
          const response = await this.createDeeplTranslation(payload);
          this.filterData(response);
          this.$toastr.s({
            title: this.$t('pages.project-translations.alert-saved'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
          this.updateLangTab();
        } catch (error) {
          this.$toastr.e(error);
        }
      },
      async show() {
        this.setPagination = true;
        if (this.withoutTranslations || this.toBeChecked || this.filter.length > 0) {
          this.setPagination = false;
        }
        await this.fetchAllTranslations({
          currentPage: this.currentPage,
          projectId: this.$route.params.id,
          languageId: this.activeTab,
          q: this.filter,
          withoutTranslations: this.withoutTranslations,
          toBeChecked: this.toBeChecked,
        });
        // this.updateLangTab();
      },
      async switchTab() {
        if (this.specificSearchResultPayload) {
          await this.getSpecificSearchResult(this.specificSearchResultPayload);
        } else {
          this.filter = '';
          await this.show();
        }
      },
      tabName(tab) {
        return `tab(${tab.id})`;
      },
      async onSubmit(row, langCode) {
        const payload = {
          translation: row.translation.draft_translation || row.translation.translation,
          language_id: this.activeTab,
          language_iso: langCode,
          keyword_id: row.keywordId,
          keywords_translated: row.translation.keywordTranslatedId,
        };
        if (payload.translation !== '') {
          if (payload.language_iso === 'en') {
            const result = await Swal.fire({
              position: 'top',
              title: 'Are you sure to update Translation for all Languages?',
              html: '<ul style="text-align:left; font-size:15px;">'
                + '<li>This will update <strong>All Translations</strong> except '
                + '<strong>"Manual"</strong> one.</li><br>'
                + '<li><strong>"Manual"</strong> status will be updated to '
                + '<strong>"To be Checked"</strong> but the <strong>Translation</strong> '
                + 'remains the same.</li></ul>',
              icon: 'warning',
              showCancelButton: true,
              cancelButtonColor: '#d33',
              confirmButtonColor: 'rgb(74 144 226)',
            });
            if (result.isConfirmed) {
              await this.callSaveApi(payload);
            }
          } else {
            await this.callSaveApi(payload);
          }
          this.updateLangTab();
        }
      },
      async callSaveApi(payload) {
        const response = await this.createProjectTranslation(payload);
        this.filterData(response);
        this.$toastr.s({
          title: this.$t('pages.project-translations.alert-saved'),
          msg: '',
          timeout: 3000,
          progressbar: false,
        });
      },
      filterData(response) {
        this.filterProjectKeys.map((item) => {
          if (item.keywordId === response.keywordId) {
            const newItem = { ...item };
            newItem.translation.translation = response.translation.translation;
            newItem.translation.draft_translation = response.translation.draft_translation;
            newItem.translation.updated_at = response.translation.updated_at;
            newItem.translation.is_automated = response.translation.is_automated;
            return newItem;
          }
          return item;
        });
      },
      async updateLangTab() {
        const toBeChecked = await this.getToBeChecked(this.$route.params.id);
        this.tabs = this.tabs.map((item) => {
          const toBeCheckedCount = toBeChecked[item.iso];
          return { ...item, toBeCheckedCount };
        });
      },
      updateCurrentPage(currentPage) {
        this.currentPage = currentPage;
        this.show();
      },
      breadCrumbs() {
        return [
          {
            text: this.$t('pages.dashboard.title'),
            to: { name: 'dashboard' },
          },
          {
            text: this.project.name,
            to: { name: 'projects' },
          },
          {
            text: this.$t('pages.project-translations.title'),
            active: true,
          },
        ];
      },
      async onSearch(event) {
        if (this.filter.length === 0 || this.filter.length >= 3) {
          this.hideResults = true;
          if (event) {
            event.target.blur();
          }
          await this.show();
        }
      },
      async filterRecords() {
        await this.show();
      },
      async displayOnlyWithoutTranslations() {
        await this.show();
      },
      descriptionLimited(description) {
        if (description.length > 20) {
          return `${description.substring(0, 20)}...`;
        }
        return description;
      },
      rulesFor(field) {
        const rules = {
          required: true,
        };
        switch (field) {
          case 'key':
            break;
          default:
            break;
        }
        return rules;
      },
      modalScroll(ev) {
        const modal = ev.target;
        this.$nextTick(() => {
          if (modal != null) modal.scrollTop = 0;
        });
      },
      resetProjectPageModal() {
        this.webpageURL = '';
        this.uploadFile = null;
        this.selectedRow = {};
        if (this.$refs.imageGenerateForm != null) {
          this.$refs.imageGenerateForm.reset();
        }
        if (this.$refs.uploadImageForm != null) {
          this.$refs.uploadImageForm.reset();
        }
      },
      openCreateForm(row, languageId, uploadImage = false) {
        this.resetProjectPageModal();
        this.selectedRow = { ...row, languageId };
        if (uploadImage) {
          this.$nextTick(this.$refs.uploadImageModal.show);
        } else {
          this.$nextTick(this.$refs.imageGenerateModal.show);
        }
      },
      async processForm() {
        try {
          const keyword = { ...this.selectedRow };

          let result = null;
          if (this.uploadFile != null) {
            const formData = new FormData();
            formData.append('keywordId', keyword.keywordId);
            formData.append('languageId', keyword.languageId);
            formData.append('webpageURL', this.webpageURL);
            formData.append('text', keyword.translation.translation);
            formData.append('imageUpload', 1);
            formData.append('image', this.uploadFile);
            // for (let [key, value] of formData.entries()) {
            //   console.log(`${key}: ${value}`);
            // }
            result = await this.uploadKeywordImage(formData);
            this.$refs.uploadImageModal.hide();
          } else {
            const payload = {
              keywordId: keyword.keywordId,
              languageId: keyword.languageId,
              webpageURL: this.webpageURL,
              text: keyword.translation.translation,
              imageUpload: 0,
              image: '',
            };

            result = await this.generateKeywordImage(payload);
            this.$refs.imageGenerateModal.hide();
          }
          this.filterProjectKeys.map((item) => {
            if (item.keywordId === result.keywordId) {
              const newItem = { ...item };
              newItem.keywordImages.id = result.id;
              newItem.keywordImages.imageURL = result.imageURL;
              newItem.keywordImages.webpageURL = result.webpageURL;
              return newItem;
            }
            return item;
          });
          this.$toastr.s({
            title: this.$t('pages.project-translations.alert-image-saved-title'),
            msg: this.$t('pages.project-translations.alert-image-saved-msg'),
            timeout: 3000,
            progressbar: false,
          });
        } catch (error) {
          this.$toastr.e(error.message, this.$t('error'));
        }
      },
      async removeImage(row) {
        await this.removeKeywordImage(row.keywordImages.id);
        this.filterProjectKeys.map((item) => {
          if (item.keywordId === row.keywordId) {
            const newItem = { ...item };
            newItem.keywordImages.id = '';
            newItem.keywordImages.imageURL = '';
            newItem.keywordImages.webpageURL = '';
            return newItem;
          }
          return item;
        });
      },
      isTranslationExcluded(iso) {
        return this.deepLExcludedLangs.includes(iso);
      },
      async handleKeyUp(event) {
        if (event.target.value === '' || event.key === 'Escape') {
          this.filter = '';
          await this.show();
        }

        if (event.key === 'Enter') {
          await this.onSearch();
        }

        this.hideResults = false;
        await this.triggerSearch();
      },
      async triggerSearch() {
        clearTimeout(this.debounceTimeout);
        const trimmedSearch = this.filter.trim();

        if (trimmedSearch?.length) {
          this.debounceTimeout = setTimeout(async () => {
            const currentTab = this.tabs.find((tab) => tab.id === this.activeTab);
            const payload = {
              q: trimmedSearch,
              activeProject: this.$route.params.id,
              activeLanguage: currentTab.iso,
            };

            await this.searchRecords(payload);
          }, 100);
        }
      },
      async getSpecificSearchResult(result) {
        const queryValue = result.translation.slice(0, 20);
        this.specificSearchResultPayload = result;

        await this.specificSearchResult({
          keywordId: result.keywordId,
          projectId: this.$route.params.id,
          languageId: this.activeTab,
        });

        setTimeout(() => {
          this.hideResults = true;
        }, 100);
        this.filter = queryValue;
        this.setPagination = false;
      },
      handleClickOutside(event) {
        const { searchResultsContainer, searchInput } = this.$refs;
        if (
          searchResultsContainer
          && !searchResultsContainer.contains(event.target)
          && searchInput
          && searchInput.$el
          && !searchInput.$el.contains(event.target)
        ) {
          this.hideResults = true;
        }
      },
      async clearSearch() {
        this.filter = '';
        this.specificSearchResultPayload = null;
        await this.show();
      },
    },
  };
</script>

<style scoped>
.dateFont {
  font-size: 12px;
}
.placeHolderString {
  margin-top: 63px;
}

.noImage {
  padding-top: 25px;
  padding-bottom: 25px;
  padding-right: 50px;
}

.hide-search-results {
  display: none;
}

.results-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 400px;
  overflow-y: auto;
}

.results-list li:last-child {
  border-bottom: none;
}

.result-item {
  cursor: pointer;
  border-bottom: 1px solid rgb(216, 216, 216);
  color: #545d6c;
  height: 32px;
  line-height: 32px;
  font-size: 14px;
  padding-left: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-item:hover {
  background-color: #f0f0f0;
}

.search-results {
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  background-color: #fff;
  z-index: 1000;
  min-width: 420px;
  max-height: 350px;
  position: absolute;
  top: 50px;
  overflow-y: auto;
}

.lessResults {
  overflow-y: none !important;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 5px;
}

.search-width {
  width: 300px;
}

.clear-icon {
  position: absolute;
  right: 160px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 30px;
  color: #b1a8a8;
}

.clear-icon:hover {
  color: #979292;
}
</style>
